import { createAuth0Client, type Auth0Client } from '@auth0/auth0-spa-js';
import { env } from '../env';
import {
  getStoredTokens,
  isTokenValid,
  setStoredTokens,
  type StoredTokens,
} from './tokenStore';

/**
 * Singleton Auth0 SPA client — used for the BROWSER popup flow only.
 * The Office Dialog flow (see officeDialogAuth.ts) manages its own tokens
 * in tokenStore.ts and does not touch this client.
 *
 * Auth0 Console requirements for the corresponding Application:
 *   - Type: Single Page Application
 *   - Token Endpoint Auth Method: None (public PKCE client)
 *   - Refresh Token Rotation: ON
 */
let clientPromise: Promise<Auth0Client> | null = null;

export function getAuth0Client(): Promise<Auth0Client> {
  if (!clientPromise) {
    clientPromise = createAuth0Client({
      domain: env.auth0.domain,
      clientId: env.auth0.clientId,
      authorizationParams: {
        redirect_uri: `${env.origin}/auth-callback.html`,
        audience: env.auth0.audience,
        scope: 'openid profile email offline_access',
      },
      cacheLocation: 'localstorage',
      useRefreshTokens: true,
      useRefreshTokensFallback: false,
    });
  }
  return clientPromise;
}

/**
 * Unified access-token getter.
 *
 * Resolution order:
 *   1. Our own token store (both Office Dialog and browser popup mirror tokens here)
 *   2. If stored but expired and we have a refresh token, refresh it
 *   3. Otherwise: fail. We DO NOT fall back to auth0-spa-js.getTokenSilently()
 *      because inside Word's WKWebView the silent-auth iframe hangs
 *      indefinitely (iframe can't communicate with Auth0), which was causing
 *      a spurious 15-second "Signing in…" stall. The Office Dialog flow is
 *      expected to have populated the store before any API call runs.
 *
 * Returns a valid access token ready to put in `Authorization: Bearer …`.
 */
export async function getAccessToken(): Promise<string> {
  const stored = getStoredTokens();
  if (stored && isTokenValid(stored)) {
    return stored.accessToken;
  }
  if (stored?.refreshToken) {
    const refreshed = await refreshViaOauthToken(stored.refreshToken);
    setStoredTokens(refreshed);
    return refreshed.accessToken;
  }
  throw new Error('No valid access token — user must re-authenticate');
}

export async function getUser() {
  const client = await getAuth0Client();
  return client.getUser();
}

export async function isAuthenticated(): Promise<boolean> {
  const client = await getAuth0Client();
  return client.isAuthenticated();
}

/**
 * Light refresh helper to avoid a circular import with officeDialogAuth.
 * Hits Auth0's /oauth/token directly. Mirrors the logic there.
 */
async function refreshViaOauthToken(refreshToken: string): Promise<StoredTokens> {
  const res = await fetch(`https://${env.auth0.domain}/oauth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      grant_type: 'refresh_token',
      client_id: env.auth0.clientId,
      refresh_token: refreshToken,
    }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Token refresh failed (${res.status}): ${text}`);
  }
  const body = (await res.json()) as {
    access_token: string;
    refresh_token?: string;
    id_token: string;
    expires_in: number;
    scope: string;
  };
  return {
    accessToken: body.access_token,
    refreshToken: body.refresh_token ?? refreshToken,
    idToken: body.id_token,
    expiresAt: Date.now() + body.expires_in * 1000,
    scope: body.scope,
  };
}
